import { formatDelta, formatNumber, formatPercent } from '../utils/formatting.js'

function metricLabel(metric) {
  return metric === 'shares' ? '持有數' : '權重'
}

function TrendSparkline({ name, trend }) {
  const points = trend?.points ?? []
  const values = points.map((point) => point.value)
  const width = 136
  const height = 30
  const padding = 3
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min
  const xStep = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0
  const coords = points.map((point, index) => {
    const x = points.length > 1 ? padding + index * xStep : width / 2
    const y = range === 0
      ? height / 2
      : height - padding - ((point.value - min) / range) * (height - padding * 2)
    return `${x},${y}`
  })

  if (points.length === 0) {
    return <span className="trend-empty">--</span>
  }

  return (
    <span className="trend-cell">
      <svg
        className="trend-sparkline"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${name} ${metricLabel(trend.metric)}趨勢線`}
      >
        <polyline points={coords.join(' ')} />
        <circle cx={coords.at(-1).split(',')[0]} cy={coords.at(-1).split(',')[1]} r="2.5" />
      </svg>
    </span>
  )
}

function sortMark(sort, field) {
  if (sort?.field !== field || !sort.direction) {
    return ''
  }
  return sort.direction === 'desc' ? '↓' : '↑'
}

function SortButton({ field, sort, onSort, ariaLabel, children }) {
  const mark = sortMark(sort, field)
  const label = ariaLabel ?? (typeof children === 'string' ? children : undefined)
  const sortLabel = label && mark ? `${label} ${mark}` : label
  return (
    <button type="button" className="th-button" aria-label={sortLabel} onClick={() => onSort(field)}>
      {children}{mark ? <span className="sort-mark" aria-hidden="true">{mark}</span> : ''}
    </button>
  )
}

export default function HoldingTable({
  rows,
  comparisonConfigs,
  sort,
  onSort,
  trendMetric,
  onTrendMetricToggle,
}) {
  if (rows.length === 0) {
    return <p className="empty-state">尚未有持股資料，請先在本機更新資料檔。</p>
  }

  return (
    <div className="table-wrap">
      <table className="holding-table">
        <thead>
          <tr>
            <th className="sticky-col code-col"><SortButton field="code" sort={sort} onSort={onSort}>代號</SortButton></th>
            <th className="sticky-col name-col"><SortButton field="name" sort={sort} onSort={onSort}>名稱</SortButton></th>
            <th className="trend-col">
              <button type="button" className="th-button" onClick={onTrendMetricToggle}>
                {`趨勢線 ${metricLabel(trendMetric)}`}
              </button>
            </th>
            <th><SortButton field="weight" sort={sort} onSort={onSort}>權重</SortButton></th>
            <th><SortButton field="shares" sort={sort} onSort={onSort}>持有數</SortButton></th>
            {comparisonConfigs.map((config) => (
              <th key={config.id}>
                <SortButton
                  field={`comparison:${config.id}`}
                  sort={sort}
                  onSort={onSort}
                  ariaLabel={`比較 ${config.id}`}
                >
                  {`${config.daysAgo}日變化 / ${config.metric === 'weight' ? '權重' : '持有數'}`}
                </SortButton>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code}>
              <td className="sticky-col code-col mono">{row.code}</td>
              <td className="sticky-col name-col">{row.name}</td>
              <td className="trend-col">
                <TrendSparkline name={row.name} trend={row.trend} />
              </td>
              <td className="numeric">{formatPercent(row.weight)}</td>
              <td className="numeric">{formatNumber(row.shares)}</td>
              {row.comparisons.map((cell) => (
                <td key={cell.id} className={`numeric delta delta--${cell.status}`}>
                  {formatDelta(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
